import { IconChevronDown } from "@components/ui/Icon"
import { cn, tv } from "@utils/tw"
import { useAppData } from "eddev/hooks"
import { Link, useHydrating } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useState } from "react"

type MenuItem = {
  label: string
  uri: string
  target?: string
  children?: MenuItem[]
}

export function MainMenu() {
  const hydrating = useHydrating()

  const menu = useAppData((data) => {
    const getItems = (parent: number | null): MenuItem[] =>
      data.mainMenu?.menuItems?.nodes
        .filter((item) => item.parentDatabaseId === parent)
        .map((item) => ({
          label: item.label!,
          uri: item.uri!,
          target: item.target!,
          children: getItems(item.databaseId)!,
        })) ?? []
    return getItems(0)
  })

  const [activeIndex, setActiveIndex] = useState<number | null>(() => {
    if (hydrating) return null
    return menu.findIndex((item) => item.uri === window.location.pathname)
  })

  return (
    <div className="flex flex-col py-4 p-[--padding] text-fg type-ui-l">
      {menu.map((item, i) => {
        const isLink = item.uri !== "#"
        return (
          <div key={i}>
            <div className="group flex items-center rounded-sm">
              {isLink ? (
                <Link
                  href={item.uri}
                  target={item.target}
                  // className="flex flex-1 rounded-sm px-3 text-fg link-active:font-semibold link-child-active:font-semibold py-2"
                  className={cn(linkStyles(), "flex-1 py-3 md:py-3")}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  className={cn(linkStyles({ isLink: false }), "flex-1 py-3 md:py-3")}
                  onClick={() => {
                    setActiveIndex(i === activeIndex ? null : i)
                  }}
                >
                  {item.label}
                </button>
              )}
              {!!item.children?.length && (
                <button
                  className="flex-none rounded-md flex items-center justify-center p-[0.2em] group-has-[a:hover]:bg-bg-high/20 hover:bg-bg-high/50 md:active:bg-bg-high/60 transition-colors"
                  onClick={(e) => {
                    setActiveIndex(i === activeIndex ? null : i)
                    e.stopPropagation()
                  }}
                >
                  <motion.div animate={{ rotate: i === activeIndex ? -180 : 0 }}>
                    <IconChevronDown className="size-icon" />
                  </motion.div>
                </button>
              )}
            </div>
            <AnimatePresence initial={false}>
              {i === activeIndex && (
                <motion.div
                  key={activeIndex}
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  className="overflow-hidden"
                >
                  {item.children && (
                    <div className="flex flex-col gap-2 pl-4 pt-1 pb-3">
                      {item.children.map((child, j) => (
                        <Link key={j} href={child.uri} target={child.target} className={cn(linkStyles(), "py-1")}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

const linkStyles = tv({
  base: [
    "underline text-fg text-left",
    "underline-offset-[0.3em] decoration-[0.5px] decoration-transparent",
    "hover:decoration-fg-secondary/60 transition-colors",
  ],
  variants: {
    isLink: {
      true: "link-active:decoration-fg-secondary/60 link-child-active:decoration-fg-secondary/60",
    },
  },
  defaultVariants: {
    isLink: true,
  },
})
